<script setup>
import { ref, onMounted, computed, onUnmounted } from "vue";
import { useStore } from "vuex";

import setTooltip from "@/assets/js/tooltip.js";
import DefaultProjectCard from "./components/DefaultProjectCard.vue";
import { CoursesClient } from "@/services/CoursesClient";
import { CategoriesClient } from '@/services/CategoriesClient';
import Swal from "sweetalert2";

const store = useStore();
const isAuthenticated = computed(() => store.state.isAuthenticated);
const coursesClient = new CoursesClient();
const categoriesClient = new CategoriesClient();
const courses = ref([]);
const filteredCourses = ref([]);
const categories = ref([]);
const loading = ref(true);
const error = ref(null);
const searchQuery = ref("");
const activeCategory = ref("all");

// Efecto máquina de escribir para títulos de cursos
const typewriterText = ref('');
const typewriterIndex = ref(0);
const typewriterCharIndex = ref(0);
const typewriterDeleting = ref(false);
const typewriterInterval = ref(null);

const getTypewriterTitles = computed(() => {
  // Array para texto por defecto
  return ['Desarrollo Web', 'Ciencia de Datos', 'Inteligencia Artificial', 'Diseño Gráfico', 'Negocios'];
});

const startTypewriter = () => {
  if (typewriterInterval.value) clearInterval(typewriterInterval.value);
  let pauseTimeout = null;
  const typeSpeed = 240; // ms por letra escribiendo
  const deleteSpeed = 180; // ms por letra borrando
  const pauseAfterWrite = 500; // ms de pausa al terminar de escribir

  const runTypewriter = () => {
    const titles = getTypewriterTitles.value;
    const currentTitle = titles[typewriterIndex.value % titles.length];
    if (!typewriterDeleting.value) {
      // Escribiendo
      typewriterText.value = currentTitle.slice(0, typewriterCharIndex.value + 1);
      typewriterCharIndex.value++;
      if (typewriterCharIndex.value === currentTitle.length) {
        // Pausa antes de borrar
        pauseTimeout = setTimeout(() => {
          typewriterDeleting.value = true;
          typewriterInterval.value = setTimeout(runTypewriter, deleteSpeed);
        }, pauseAfterWrite);
        return;
      }
      typewriterInterval.value = setTimeout(runTypewriter, typeSpeed);
    } else {
      // Borrando
      typewriterText.value = currentTitle.slice(0, typewriterCharIndex.value - 1);
      typewriterCharIndex.value--;
      if (typewriterCharIndex.value === 0) {
        typewriterDeleting.value = false;
        typewriterIndex.value = (typewriterIndex.value + 1) % titles.length;
        typewriterInterval.value = setTimeout(runTypewriter, typeSpeed);
        return;
      }
      typewriterInterval.value = setTimeout(runTypewriter, deleteSpeed);
    }
  };
  runTypewriter();
};

if (window.innerWidth <= 768) {
  store.commit("navbarMinimize");
}

const fetchCourses = async () => {
  try {
    const response = await coursesClient.getAllCourses();

    // Filter out courses with estado = 0
    const mappedCourses = response.data
      .filter(course => course.estado !== 0) // Add this filter
      .map(course => ({
        id: course.id,
        title: course.titulo,
        category: course.categoria_nombre,
        categoryId: course.categoria_id,
        chapters: course.capitulos,
        price: parseFloat(course.precio),
        currency: course.moneda,
        teacher: course.profesor_nombre,
        image: null,
        portadaKey: course.portada?.url,
        action: {
          color: "success",
          label: "Ver Curso",
          route: `/cursos/${course.id}`
        }
      }));

    const coursesWithImages = await Promise.all(
      mappedCourses.map(async course => {
        if (course.portadaKey) {
          try {
            const imageUrl = await getImageUrl(course.portadaKey);
            return { ...course, image: imageUrl };
          } catch (err) {
            console.error(`Error loading image for course ${course.id}:`, err);
            return { ...course, image: require("@/assets/img/home-decor-1.jpg") };
          }
        }
        return { ...course, image: require("@/assets/img/home-decor-1.jpg") };
      })
    );

    courses.value = coursesWithImages;
    filteredCourses.value = coursesWithImages;
  } catch (err) {
    error.value = "Failed to load courses";
    console.error("Error loading courses:", err);
  } finally {
    loading.value = false;
  }
};

const fetchCategories = async () => {
  try {
    const response = await categoriesClient.getAll();
    categories.value = response;
    console.log("Categories fetched:", response);
  } catch (error) {
    console.error('Error fetching categories:', error);
    Swal.fire('Error', 'No se pudieron cargar las categorías', 'error');
  }
};

const getImageUrl = async (s3Key) => {
  const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${s3Key}`;
  const response = await fetch(url, {
    headers: {
      Accept: 'text/plain',
      Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
    }
  });

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return await response.text();
};

const filterCourses = () => {
  // Start with all visible courses (already filtered in fetchCourses)
  let results = [...courses.value];

  // Apply category filter
  if (activeCategory.value !== "all") {
    results = results.filter(course => course.categoryId == activeCategory.value);
  }

  // Apply search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    results = results.filter(course =>
      course.title.toLowerCase().includes(query) ||
      course.category.toLowerCase().includes(query) ||
      course.teacher.toLowerCase().includes(query)
    );
  }

  filteredCourses.value = results;
};

const setCategoryFilter = (categoryId) => {
  activeCategory.value = categoryId;
  filterCourses();
};

onMounted(() => {
  if (!isAuthenticated.value) {
    console.log("User is NOT authenticated - hiding sidebar and footer");
    store.state.showSidenav = false;
  } else {
    console.log("User is authenticated - keeping default layout");
    store.state.showSidenav = true;
  }
  setTooltip(store.state.bootstrap);
  fetchCourses();
  fetchCategories();
  startTypewriter();
});

onUnmounted(() => {
  console.log("Component unmounted - resetting sidebar visibility");
  store.state.showSidenav = true;
  if (typewriterInterval.value) clearInterval(typewriterInterval.value);
});

</script>

<template>
  <div class="maintenance-banner">
    <div class="maintenance-content ">
      <div class="maintenance-icon">
        <i class="fas fa-cog fa-spin"></i>
      </div>
      <div class="maintenance-text ">
        <span>Sitio en Mantenimiento - Estamos implementando mejoras</span>
      </div>
    </div>
  </div>

  <div class="browse-page-bg">

    <!-- Hero -->
    <section class="hero-section d-flex align-items-center justify-content-center flex-column text-center">
      <div class="container">
        <div class="row align-items-center justify-content-center flex-column flex-lg-row text-lg-start text-center">
          <!-- Lado izquierdo: logo y texto -->
          <div class="col-lg-6 mb-4 mb-lg-0 d-flex flex-column align-items-center align-items-lg-start">
            <img src="@/assets/AppLogoFull.png" alt="Logo" class="hero-logo mb-3 mobile-logo" />
            <h1 class="hero-title mb-3">Aprende, Crece y Transforma tu Futuro</h1>
            <p class="hero-subtitle mb-4">Explora cursos de alta calidad en tecnología, negocios, diseño y más.
              ¡Comienza hoy mismo!</p>
            <a href="#cursos-populares" class="btn btn-lg btn-primary rounded-pill px-5 py-2 shadow">Ver Cursos
              Populares</a>
          </div>
          <!-- Lado derecho: animación máquina de escribir -->
          <div class="col-lg-6 d-flex justify-content-center justify-content-lg-end">
            <div class="typewriter-searchbar mx-auto mb-3 mb-lg-0">
              <i class="fas fa-search searchbar-icon"></i>
              <span class="typewriter-text">{{ typewriterText }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Grilla de cursos y filtros -->
    <section class="container-fluid py-4">
      <div class="row mb-4">
        <div class="col-12">
          <div class="card p-3 p-md-4 shadow-sm">
            <!-- Tags de categorías -->
            <div class="row mb-3">
              <div class="col-12 d-flex flex-wrap gap-2 justify-content-center filters">
                <button class="btn rounded-pill" :class="activeCategory === 'all' ? 'btn-dark' : 'btn-outline-dark'"
                  @click="setCategoryFilter('all')">
                  <i class="fas fa-list pe-1"></i> Todos
                </button>
                <button v-for="category in categories" :key="category.id" class="btn rounded-pill"
                  :class="activeCategory == category.id ? 'btn-dark' : 'btn-outline-secondary'"
                  @click="setCategoryFilter(category.id)">
                  <i v-if="category.icono" :class="`${category.icono} pe-1`"></i>
                  {{ category.nombre }}
                </button>
              </div>
            </div>
            <!-- Barra de búsqueda -->
            <div class="row mb-3">
              <div class="col-12 d-flex align-items-center justify-content-center">
                <div class="modern-searchbar w-100">
                  <i class="fas fa-search searchbar-icon"></i>
                  <input v-model="searchQuery" @input="filterCourses" class="form-control modern-input"
                    placeholder="Buscar cursos por nombre o categoría" type="text" />
                  <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''; filterCourses();"
                    aria-label="Limpiar búsqueda">
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
            </div>
            <hr class="horizontal dark my-2" />
            <div class="p-3 card-body">
              <div v-if="loading" class="text-center py-9">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <div v-else-if="error" class="alert alert-danger">
                {{ error }}
              </div>
              <div v-else>
                <div v-if="filteredCourses.length === 0" class="text-center py-7">
                  No se encontraron cursos que coincidan con tu búsqueda.
                </div>
                <div v-else>
                  <div class="row g-4 justify-content-start course-gallery-row">
                    <div v-for="course in filteredCourses" :key="course.id" class="col-lg-3 col-md-4 col-sm-6 col-12">
                      <default-project-card :id="course.id" :img="course.image" :title="course.title"
                        :category="course.category" :chapters="course.chapters" :currency="course.currency"
                        :price="course.price" :teacher="course.teacher" :action="course.action" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.browse-page-bg {
  background: linear-gradient(135deg, #f8fafc 0%, #e3e9f7 100%);
  min-height: 100vh;
}

.hero-section {
  min-height: 60vh;
  background: linear-gradient(332deg, #2152ff 0%, #21d4fd 100%);
  color: #fff;
  padding: 60px 0 40px 0;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-logo {
  max-width: 180px;
  filter: drop-shadow(0 4px 24px rgba(33, 82, 255, 0.15));
}

.hero-title {
  font-size: 2.7rem;
  font-weight: 800;
  letter-spacing: -1px;
  margin-bottom: 0.5rem;
}

.hero-subtitle {
  font-size: 1.25rem;
  font-weight: 400;
  margin-bottom: 1.5rem;
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-logo {
    max-width: 120px;
  }

  .hero-section .row {
    flex-direction: column !important;
  }
}

@media (max-width: 576px) {
  .hero-title {
    font-size: 1.3rem;
  }

  .hero-logo {
    max-width: 80px;
  }
}

.filters {
  margin-bottom: 0.5rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.typewriter-text {
  border-right: 2px solid #fff;
  padding-right: 4px;
  animation: blink-caret 0.8s step-end infinite;
  color: #fff;
}

@keyframes blink-caret {

  from,
  to {
    border-color: transparent;
  }

  50% {
    border-color: #fff;
  }
}

.card .card-blog {
  color: #000000;
}

.card .form-control {
  max-width: 400px;
}

@media (min-width: 768px) {
  .card .form-control {
    margin-left: 0.5rem;
    margin-right: 0.5rem;
  }
}

.motion-highlight-card {
  position: relative;
  transition: box-shadow 0.3s, border-color 0.3s;
  z-index: 1;
  border-radius: 10px;
}

.motion-highlight-card:hover {
  box-shadow: 0 0 0 4px #f1f1f1;
  border-radius: 10px;
  /* animation: highlight-motion 1.2s linear infinite; */
  border: 0.5px solid #f1f1f1;
}

/* --- HERO TYPEWRITER SEARCHBAR --- */
.typewriter-searchbar {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 2.5rem;
  box-shadow: 0 4px 32px rgba(33, 82, 255, 0.10);
  padding: 1.1rem 2.2rem 1.1rem 1.5rem;
  min-width: 320px;
  max-width: 520px;
  color: #2152ff;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-size: 1.35rem;
  position: relative;
  border: 2px solid #e3e9f7;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.typewriter-searchbar:hover,
.typewriter-searchbar:focus-within {
  box-shadow: 0 8px 32px rgba(33, 82, 255, 0.18);
  border-color: #21d4fd;
}

.searchbar-icon {
  font-size: 1.5rem;
  margin-right: 1rem;
  color: #21d4fd;
}

.typewriter-text {
  border-right: 2px solid #2152ff;
  padding-right: 4px;
  animation: blink-caret 0.8s step-end infinite;
  color: #2152ff;
  font-weight: 600;
  letter-spacing: 0.5px;
  min-height: 1.5em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes blink-caret {

  from,
  to {
    border-color: transparent;
  }

  50% {
    border-color: #2152ff;
  }
}

/* Centrar y limitar el ancho de la galería de cursos */
.course-gallery-row {
  max-width: 85em;
  margin-left: auto;
  margin-right: auto;
}

.modern-searchbar {
  position: relative;
  display: flex;
  align-items: center;
  background: #f5f7fa;
  border-radius: 2.2rem;
  box-shadow: 0 2px 12px rgba(33, 82, 255, 0.07);
  padding: 0.2rem 1.2rem 0.2rem 0.8rem;
  min-width: 220px;
  max-width: 480px;
  width: 100%;
  transition: box-shadow 0.2s, border-color 0.2s;
  border: 1.5px solid #e3e9f7;
}

.modern-searchbar:focus-within {
  box-shadow: 0 4px 24px rgba(33, 82, 255, 0.13);
  border-color: #21d4fd;
  background: #fff;
}

.searchbar-icon {
  font-size: 1.2rem;
  color: #2152ff;
  margin-right: 0.7rem;
  opacity: 0.7;
}

.modern-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1.13rem;
  color: #222b45;
  width: 100%;
  padding: 0.7rem 0.5rem 0.7rem 0;
  box-shadow: none;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
}

.modern-input::placeholder {
  color: #a0aec0;
  opacity: 1;
  font-weight: 400;
  letter-spacing: 0.02em;
}

.clear-btn {
  background: none;
  border: none;
  outline: none;
  color: #b0b8c9;
  font-size: 1.1rem;
  cursor: pointer;
  margin-left: 0.3rem;
  transition: color 0.18s;
  padding: 0 0.2rem;
}

.clear-btn:hover {
  color: #2152ff;
}

@media (max-width: 576px) {
  .modern-searchbar {
    max-width: 100%;
    padding: 0.1rem 0.7rem 0.1rem 0.5rem;
  }

  .modern-input {
    font-size: 1rem;
    padding: 0.5rem 0.2rem 0.5rem 0;
  }
}
/* Slim Maintenance Banner Styles */
.maintenance-banner {
  background: linear-gradient(135deg, #ffe882 0%, #fac25b 100%);
  color: #855c04;
  padding: 8px 0;
  border-bottom: 1px solid #ffc77e;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
  position: relative;
}

.maintenance-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.maintenance-icon {
  font-size: 16px;
  color: #f59301;
}


/* Animation for the icon */
.fa-spin {
  animation: fa-spin 6s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .maintenance-text span {
    font-size: 13px;
  }
  
  .maintenance-content {
    gap: 8px;
  }
}

.hero-logo {
  width: auto;
  height: 60px; /* or your current desktop size */
}

/* Mobile size (adjust breakpoint as needed) */
@media (max-width: 767.98px) {
  .mobile-logo {
    height: 90px !important; /* 50% larger for mobile */
    max-width: 100% !important; /* Ensure it stays responsive */
  } 
  }
</style>